'use client'

import { Box } from '@chakra-ui/react'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { CorporatePresentationSection } from '@/components/InvestorRelations/CorporatePresentationSection'
import { DocumentsSection } from '@/components/InvestorRelations/DocumentsSection'
import { DynamicFontLoader } from '@/components/InvestorRelations/DynamicFontLoader'
import { FooterSection } from '@/components/InvestorRelations/FooterSection'
import { FundraisingCampaignSection } from '@/components/InvestorRelations/FundraisingCampaignSection'
import { InvestorRelationsHeader } from '@/components/InvestorRelations/InvestorRelationsHeader'
import { InvestorRelationsHero } from '@/components/InvestorRelations/InvestorRelationsHero'
import { InvestorRelationsPageHead } from '@/components/InvestorRelations/InvestorRelationsPageHead'
import { MarketDataSection } from '@/components/InvestorRelations/MarketDataSection'
import { RegisterInterestSection } from '@/components/InvestorRelations/RegisterInterestSection'
import { ResultsSection } from '@/components/InvestorRelations/ResultsSection'
import type {
  IRDocument,
  InvestorRelationsConfig,
  InvestorRelationsConfigContextType,
} from '@/utils/investorRelations'

const InvestorRelationsSiteConfigContext = createContext<
  InvestorRelationsConfigContextType | undefined
>(undefined)

function useInvestorRelationsSiteConfig() {
  const context = useContext(InvestorRelationsSiteConfigContext)
  if (!context) {
    throw new Error(
      'useInvestorRelationsSiteConfig must be used inside InvestorRelationsSite'
    )
  }
  return context
}

export interface InvestorRelationsSiteDocuments {
  corporate: IRDocument[]
  regulatory: IRDocument[]
  reports: IRDocument[]
  documents: IRDocument[]
  fundraisingPresentation: IRDocument[]
  fundraisingTerms: IRDocument[]
  fundraisingDocuments: IRDocument[]
}

export interface InvestorRelationsSiteProps {
  config: InvestorRelationsConfig
  documents: InvestorRelationsSiteDocuments
  loading?: boolean
  error?: Error | null
  documentLoading?: Partial<
    Record<'corporate' | 'regulatory' | 'reports' | 'documents', boolean>
  >
  isPreview?: boolean
  organisationId?: string
  deepLinkedDocumentId?: string | null
  deepLinkedSectionId?: string | null
  onDeepLinkedDocumentClose?: (sectionId: string) => void
  onDocumentOpen?: (sectionId: string, documentId: string) => void
  children?: ReactNode
}

export function InvestorRelationsSite({
  config,
  documents,
  loading = false,
  error = null,
  documentLoading = {},
  isPreview = false,
  organisationId,
  deepLinkedDocumentId = null,
  deepLinkedSectionId = null,
  onDeepLinkedDocumentClose,
  onDocumentOpen,
  children,
}: InvestorRelationsSiteProps) {
  const corporatePresentationRef = useRef<HTMLElement>(null)
  const regulatoryNewsRef = useRef<HTMLElement>(null)
  const marketDataRef = useRef<HTMLElement>(null)
  const reportsRef = useRef<HTMLElement>(null)
  const documentsRef = useRef<HTMLElement>(null)
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null)

  const fundraisingConfig = config.fundraisingConfig
  const fundraisingEnabled = fundraisingConfig?.enabled === true
  const firstSectionId = fundraisingEnabled
    ? 'fundraising-campaign'
    : 'corporate-presentation'

  const scrollToSection = useCallback((sectionId: string) => {
    const refMap: Record<string, React.RefObject<HTMLElement | null>> = {
      'corporate-presentation': corporatePresentationRef,
      'fundraising-campaign': corporatePresentationRef,
      'regulatory-news': regulatoryNewsRef,
      'market-data': marketDataRef,
      results: reportsRef,
      documents: documentsRef,
    }
    const target = refMap[sectionId]?.current
    if (!target) return
    const elementTop = target.getBoundingClientRect().top + window.scrollY - 50
    window.scrollTo({ top: elementTop, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    const entries: Array<[string, React.RefObject<HTMLElement | null>]> = [
      [firstSectionId, corporatePresentationRef],
      ['regulatory-news', regulatoryNewsRef],
      ['market-data', marketDataRef],
      ['results', reportsRef],
      ['documents', documentsRef],
    ]
    const updateActiveSection = () => {
      const scrollPosition = window.scrollY + 150
      let active: string | null = null
      let minimumDistance = Number.POSITIVE_INFINITY
      for (const [id, ref] of entries) {
        const target = ref.current
        if (!target) continue
        const distance =
          scrollPosition - (target.getBoundingClientRect().top + window.scrollY)
        if (distance >= 0 && distance < minimumDistance) {
          minimumDistance = distance
          active = id
        }
      }
      setActiveSectionId(active)
    }
    updateActiveSection()
    window.addEventListener('scroll', updateActiveSection, { passive: true })
    return () => window.removeEventListener('scroll', updateActiveSection)
  }, [firstSectionId])

  useEffect(() => {
    let animationFrameId: number | null = null
    const scrollToCurrentSection = () => {
      const sectionId =
        deepLinkedSectionId || window.location.hash.replace(/^#/, '')
      if (!sectionId) return
      animationFrameId = window.requestAnimationFrame(() =>
        scrollToSection(sectionId)
      )
    }
    scrollToCurrentSection()
    window.addEventListener('hashchange', scrollToCurrentSection)
    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId)
      }
      window.removeEventListener('hashchange', scrollToCurrentSection)
    }
  }, [deepLinkedSectionId, scrollToSection])

  const fontFamily = config.themeConfig?.fontStyle

  return (
    <InvestorRelationsSiteConfigContext.Provider
      value={{ config, loading, error }}
    >
      <DynamicFontLoader fontFamily={fontFamily} />
      <InvestorRelationsPageHead
        useContextFunction={useInvestorRelationsSiteConfig}
        isPreview={isPreview}
        organisationId={organisationId}
      />
      <Box as="main" bg="gray.50" minH="100vh" fontFamily={fontFamily}>
        <InvestorRelationsHeader
          onNavigateToSection={scrollToSection}
          activeSectionId={activeSectionId}
          useContextFunction={useInvestorRelationsSiteConfig}
          isPreview={isPreview}
          organisationId={organisationId}
          fundraisingEnabled={fundraisingEnabled}
        />
        {!fundraisingEnabled && (
          <InvestorRelationsHero
            useContextFunction={useInvestorRelationsSiteConfig}
            isPreview={isPreview}
            organisationId={organisationId}
          />
        )}
        {fundraisingEnabled && fundraisingConfig ? (
          <FundraisingCampaignSection
            ref={corporatePresentationRef}
            config={fundraisingConfig}
            presentationDocuments={documents.fundraisingPresentation}
            termDocuments={documents.fundraisingTerms}
            supportingDocuments={documents.fundraisingDocuments}
            deepLinkedDocumentId={deepLinkedDocumentId}
            onDeepLinkedDocumentClose={onDeepLinkedDocumentClose}
            onDocumentOpen={onDocumentOpen}
            useContextFunction={useInvestorRelationsSiteConfig}
            isPreview={isPreview}
            organisationId={organisationId}
          />
        ) : (
          <CorporatePresentationSection
            ref={corporatePresentationRef}
            presentations={documents.corporate}
            deepLinkedDocumentId={deepLinkedDocumentId}
            onDeepLinkedDocumentClose={onDeepLinkedDocumentClose}
            onDocumentOpen={onDocumentOpen}
            useContextFunction={useInvestorRelationsSiteConfig}
            isPreview={isPreview}
            organisationId={organisationId}
          />
        )}
        <ResultsSection
          ref={reportsRef}
          results={documents.reports}
          isLoading={documentLoading.reports || false}
          deepLinkedDocumentId={deepLinkedDocumentId}
          onDeepLinkedDocumentClose={onDeepLinkedDocumentClose}
          onDocumentOpen={onDocumentOpen}
          useContextFunction={useInvestorRelationsSiteConfig}
          isPreview={isPreview}
          organisationId={organisationId}
        />
        <MarketDataSection
          ref={marketDataRef}
          useContextFunction={useInvestorRelationsSiteConfig}
        />
        <DocumentsSection
          ref={regulatoryNewsRef}
          documents={documents.regulatory}
          isLoading={documentLoading.regulatory || false}
          title="Regulatory News"
          backgroundColor={true}
          sectionId="regulatory-news"
          deepLinkedDocumentId={deepLinkedDocumentId}
          onDeepLinkedDocumentClose={onDeepLinkedDocumentClose}
          onDocumentOpen={onDocumentOpen}
          useContextFunction={useInvestorRelationsSiteConfig}
          isPreview={isPreview}
          organisationId={organisationId}
        />
        <DocumentsSection
          ref={documentsRef}
          documents={documents.documents}
          isLoading={documentLoading.documents || false}
          title="Documents"
          backgroundColor={false}
          sectionId="documents"
          deepLinkedDocumentId={deepLinkedDocumentId}
          onDeepLinkedDocumentClose={onDeepLinkedDocumentClose}
          onDocumentOpen={onDocumentOpen}
          useContextFunction={useInvestorRelationsSiteConfig}
          isPreview={isPreview}
          organisationId={organisationId}
        />
        <RegisterInterestSection
          companySlug={config.companySlugPublished || ''}
          useContextFunction={useInvestorRelationsSiteConfig}
        />
        <FooterSection useContextFunction={useInvestorRelationsSiteConfig} />
        {children}
      </Box>
    </InvestorRelationsSiteConfigContext.Provider>
  )
}
